import { Sprite, Text, type Texture } from 'pixi.js';
import { RARITY_META, type Rarity } from '@goodtime/shared';
import { BoardDialog } from '../board/BoardDialog';
import { animate, COLOURS, displayStyle, ease, prefersReducedMotion, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';

/** How long a find shows before it goes by itself. */
const SHOW_MS = 3400;

/** One special item found on the reels. */
export interface Find {
  name: string;
  glyph: string;
  rarity: Rarity;
  /** Owned after this find, and how many there are in all. */
  collected: number;
  total: number;
  /** Its painting, when it has one (the legendary relics do). */
  art: Texture | null;
  /** What to say under its name, in place of "N of M collected". */
  line?: string;
}

/**
 * A special item found on the reels, on a board (producer 2026-10-14: no
 * plain dialogs; it replaces the purple card): its rarity on the plaque,
 * the item glowing in its rarity's colour, its name, and how many of the
 * collection the player has. A legendary find gets confetti. It goes by
 * itself after a few seconds, or at a tap.
 */
export class FindReveal extends BoardDialog {
  private readonly glow = new Sprite(glowTexture());
  private readonly relic = new Sprite();
  private readonly glyph: Text;
  private readonly nameText: Text;
  private readonly progress: Text;
  private done: (() => void) | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly floor: () => Texture | null) {
    super({ title: 'A FIND!', floor: floor(), maxWidth: 340, titleSize: 18 });
    this.glow.anchor.set(0.5);
    this.glow.blendMode = 'add';
    this.relic.anchor.set(0.5);
    this.glyph = new Text({ text: '', style: { fontSize: 84 } });
    this.glyph.anchor.set(0.5);
    this.nameText = new Text({ text: '', style: { ...displayStyle(26), align: 'center', wordWrap: true } });
    this.nameText.anchor.set(0.5, 0);
    this.progress = new Text({ text: '', style: { ...uiStyle(15, COLOURS.cream, '600'), align: 'center', wordWrap: true } });
    this.progress.anchor.set(0.5, 0);
    this.content.addChild(this.glow, this.relic, this.glyph, this.nameText, this.progress);
    this.onDimTap = () => this.finish();
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): { open: boolean; title: string; name: string; progress: string } {
    return { open: this.visible, title: this.titleText, name: this.nameText.text, progress: this.progress.text };
  }

  /** Show a find; resolves when it has gone. */
  async play(find: Find): Promise<void> {
    const meta = RARITY_META[find.rarity];
    this.setTitle(`${meta.label.toUpperCase()} FIND!`);
    this.setFloor(this.floor());
    this.glow.tint = parseInt(meta.accent.slice(1), 16);
    if (find.art) this.relic.texture = find.art;
    this.relic.visible = find.art !== null;
    this.glyph.visible = find.art === null;
    this.glyph.text = find.glyph;
    this.nameText.text = find.name;
    this.progress.text = find.line ?? `${find.collected} of ${find.total} collected`;
    this.arrange();
    const gone = new Promise<void>((resolve) => (this.done = resolve));
    await this.open();
    if (!prefersReducedMotion()) {
      void animate(520, (t) => {
        const s = 0.6 + 0.4 * ease.outBack(t);
        this.relic.scale.set(this.relicScale * s);
        this.glyph.scale.set(s);
        this.glow.alpha = 0.9 * Math.min(1, t * 1.5);
      });
    }
    if (find.rarity === 'legendary') void this.celebrate({ x: this.contentWidth / 2, y: 70 });
    this.timer = setTimeout(() => this.finish(), SHOW_MS);
    return gone;
  }

  destroy(options?: Parameters<BoardDialog['destroy']>[0]): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.done = null;
    super.destroy(options);
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  private relicScale = 1;

  private arrange(): void {
    if (!this.progress) return;
    const w = this.contentWidth;
    const size = 128;
    this.relicScale = size / Math.max(1, this.relic.texture.width, this.relic.texture.height);
    this.relic.scale.set(this.relicScale);
    this.relic.position.set(w / 2, 8 + size / 2);
    this.glyph.position.set(w / 2, 8 + size / 2);
    this.glow.width = this.glow.height = size * 2;
    this.glow.position.set(w / 2, 8 + size / 2);
    this.nameText.style.wordWrapWidth = w - 8;
    this.nameText.position.set(w / 2, size + 18);
    this.progress.style.wordWrapWidth = w - 8;
    this.progress.position.set(w / 2, this.nameText.y + this.nameText.height + 4);
    const h = this.progress.y + this.progress.height + 6;
    if (Math.abs(h - this.lastH) > 1) {
      this.lastH = h;
      this.setContentHeight(h);
    }
  }

  private lastH = 0;

  private finish(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const done = this.done;
    this.done = null;
    if (!done) return;
    void this.close().then(done);
  }
}
